import { Grid, IconButton, InputAdornment } from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import DeleteIcon from "@material-ui/icons/Delete";
import PublishIcon from "@material-ui/icons/Publish";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { AutocompleteInput, Button, CloneButton, Confirm, Create, CreateButton, Datagrid, DeleteButton, Edit, EditButton, Filter, FormDataConsumer, FunctionField, ImageField, ImageInput, List, RadioButtonGroupInput, ReferenceField, ReferenceInput, required, SaveButton, SelectInput, SimpleForm, TextField, TextInput, Toolbar, TopToolbar, useDataProvider, useNotify, useRecordContext } from "react-admin";
import { useForm, useFormState } from "react-final-form";
import * as XLSX from "xlsx";
import dataProvider from "../../dataProvider";
import { MyDateTimeInput } from "../../MyDateTimeInput";

const contentTypeChoices = [
  { id: "programme", name: "Programme" },
  { id: "episode", name: "Episode" },
  { id: "page", name: "Page" },
  { id: "clip", name: "Clip" },
  { id: "live", name: "Live" },
];

const statusChoices = [
  { id: "preview", name: "Preview" },
  { id: "live", name: "Live" },
  { id: "end", name: "End" },
];

const versionTypeChoices = [
  { id: "rerun", name: "Rerun" },
  { id: "catchup", name: "Catchup" },
  { id: "gold", name: "Gold First" },
];

const displayPeriodChoices = [
  { id: "end", name: "Programme end" },
  { id: "start", name: "Programme start" },
];

const getChoiceName = (choices, value) => {
  const choice = choices.find((item) => String(item.id).toLowerCase() === String(value || "").toLowerCase());
  return choice ? choice.name : value;
};

const formatHKDate = (value) => {
  return value ? moment.tz(value, "Asia/Hong_Kong").format("YYYY-MM-DD") : "";
};

const formatTime = (value) => {
  const time = moment(value, ["HH:mm:ss", "HH:mm"], true);
  return time.isValid() ? time.format("HHmm") : "";
};

const getRecordStatus = (record) => String(record?.status || "").toLowerCase();
const deleteButtonStyle = { color: "red" };

const LiveToContentActionButton = () => {
  const [showConfirm, setShowConfirm] = React.useState(false);
  const record = useRecordContext();
  const notify = useNotify();
  const status = getRecordStatus(record);

  if (status === "preview") {
    return <DeleteButton record={record} resource="live-to-content-schedules" basePath="/live-to-content-schedules" undoable={false} style={deleteButtonStyle} />;
  }

  if (status !== "live") {
    return null;
  }

  return (
    <>
      <Button label="End" onClick={() => setShowConfirm(true)} style={deleteButtonStyle}>
        <DeleteIcon />
      </Button>
      <Confirm
        isOpen={showConfirm}
        title="Are you sure you want to end this item?"
        onConfirm={async () => {
          try {
            const now = moment.tz("Asia/Hong_Kong");
            const data = { ...record };

            await dataProvider.emergencyEndLiveToContentSchedule(record.id, {
              ...data,
              end_date: now.toISOString(),
              end_time: now.format("HH:mm:ss"),
            });

            window.location.reload(true);
          } catch (error) {
            notify(error.message.toString(), "warning");
          }

          setShowConfirm(false);
        }}
        onClose={() => setShowConfirm(false)}
      />
    </>
  );
};

const LiveToContentFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Channel Content ID" source="network_code" alwaysOn />
    <TextInput label="Programme ID" source="target_epg_programme_id" alwaysOn />
    <SelectInput label="Link Content Type" source="type" choices={contentTypeChoices} alwaysOn />
    <SelectInput label="Status" source="status" choices={statusChoices} alwaysOn />
  </Filter>
);

const toImportValue = (value) => (value === undefined || value === null ? "" : String(value).trim());

const formatImportDate = (value) => {
  const date = moment.tz(toImportValue(value), ["YYYYMMDD", "YYYY-MM-DD", "YYYY/M/D"], true, "Asia/Hong_Kong");
  return date.isValid() ? date.format("YYYY-MM-DD") : toImportValue(value);
};

const formatImportTime = (value) => {
  const time = moment(toImportValue(value), ["HHmm", "HH:mm", "HH:mm:ss"], true);
  return time.isValid() ? time.format("HH:mm:ss") : toImportValue(value);
};

const importColumn = {
  channelContentId: 0,
  programmeId: 1,
  contentType: 2,
  startDate: 3,
  endDate: 4,
  startTime: 5,
  endTime: 6,
  displayPeriod: 7,
  programmeLinkId: 8,
  episodeLinkId: 9,
  versionType: 10,
  liveChannelLinkId: 12,
  descriptionTc: 13,
  descriptionEn: 14,
  clipLinkId: 15,
  pageLinkId: 16,
};

const mapImportRowToSchedule = (row) => {
  const type = toImportValue(row[importColumn.contentType]).toLowerCase();
  const destinationColumnByType = {
    programme: importColumn.programmeLinkId,
    episode: importColumn.episodeLinkId,
    live: importColumn.liveChannelLinkId,
    clip: importColumn.clipLinkId,
    page: importColumn.pageLinkId,
  };

  return {
    network_code: toImportValue(row[importColumn.channelContentId]),
    target_epg_programme_id: toImportValue(row[importColumn.programmeId]),
    type,
    start_date: formatImportDate(row[importColumn.startDate]),
    end_date: formatImportDate(row[importColumn.endDate]),
    start_time: formatImportTime(row[importColumn.startTime]),
    end_time: formatImportTime(row[importColumn.endTime]),
    position: toImportValue(row[importColumn.displayPeriod]),
    destination_id: toImportValue(row[destinationColumnByType[type]]),
    version_type: toImportValue(row[importColumn.versionType]),
    description_tc: toImportValue(row[importColumn.descriptionTc]),
    description_en: toImportValue(row[importColumn.descriptionEn]),
  };
};

const LiveToContentImportButton = () => {
  const inputRef = React.useRef();
  const notify = useNotify();

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", raw: false });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: "" });
      const [, ...dataRows] = rows;
      const schedules = dataRows.filter((row) => row.some((value) => toImportValue(value))).map(mapImportRowToSchedule);

      if (!schedules.length) {
        notify("The XLSX file does not contain any schedule rows.", "warning");
        return;
      }

      await dataProvider.bulkCreateLiveToContentSchedules(schedules);
      notify(`${schedules.length} live-to-content schedule(s) imported.`, "info");
      window.location.reload(true);
    } catch (error) {
      notify(error.message?.toString() || "Unable to import the XLSX file.", "warning");
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept=".xlsx" hidden onChange={handleImport} />
      <Button label="Import" onClick={() => inputRef.current?.click()}>
        <PublishIcon />
      </Button>
    </>
  );
};

const LiveToContentListActions = () => (
  <TopToolbar>
    <CreateButton basePath="/live-to-content-schedules" />
    <LiveToContentImportButton />
  </TopToolbar>
);

export const LiveToContentList = (props) => (
  <List {...props} sort={{ field: "created_at", order: "DESC" }} bulkActionButtons={false} filters={<LiveToContentFilter />} actions={<LiveToContentListActions />}>
    <Datagrid>
      <TextField label="Channel Content ID" source="network_code" />
      <TextField label="Programme ID" source="target_epg_programme_id" />
      <ReferenceField label="Programme Name TC" source="target_epg_programme_id" reference="progammes-by-prorgamme_id" link={false}>
        <TextField source="name_tc" />
      </ReferenceField>
      <FunctionField label="Link Content Type" render={(record) => getChoiceName(contentTypeChoices, record.type)} />
      <FunctionField
        label="Status"
        render={(record) => {
          const status = getChoiceName(statusChoices, record.status);
          return <span style={getRecordStatus(record) === "live" ? { color: "red" } : undefined}>{status}</span>;
        }}
      />
      <FunctionField label="Scheduled Start Date" sortBy="start_date" render={(record) => formatHKDate(record.start_date)} />
      <FunctionField label="Scheduled End Date" sortBy="end_date" render={(record) => formatHKDate(record.end_date)} />
      <FunctionField label="Programme Period Start Time" render={(record) => formatTime(record.start_time)} />
      <FunctionField label="Programme Period End Time" render={(record) => formatTime(record.end_time)} />
      <EditButton />
      <CloneButton />
      <LiveToContentActionButton />
    </Datagrid>
  </List>
);

const LiveToContentCreateToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton />
  </Toolbar>
);

const LiveToContentEditToolbar = (props) => {
  const record = useRecordContext();

  return (
    <Toolbar {...props}>
      <SaveButton disabled={getRecordStatus(record || props.record) === "end"} />
    </Toolbar>
  );
};

const AutoCompleteProgramme = ({ autocompleteSource = "programme_autocomplete", programmeIdSource = "destination_id", captionTcSource, captionEnSource, landscapePosterSource, portraitPosterSource }) => {
  const form = useForm();
  const dataProvider = useDataProvider();

  const fillProgrammeFields = async (programmeId) => {
    if (!programmeId) {
      return;
    }

    const result = await dataProvider.getProgrammeByProgrammeId(parseInt(programmeId));

    if (result && result.data && result.data.name_tc) {
      form.change(programmeIdSource, programmeId);

      if (captionTcSource) {
        form.change(captionTcSource, result.data.name_tc);
      }

      if (captionEnSource) {
        form.change(captionEnSource, result.data.name_en);
      }

      if (landscapePosterSource) {
        form.change(landscapePosterSource, result.data.image?.landscape_medium);
      }

      if (portraitPosterSource) {
        form.change(portraitPosterSource, result.data.image?.portrait_medium);
      }
    }
  };

  return (
    <ReferenceInput
      label="Search Programme Name"
      source={autocompleteSource}
      reference="programmes-autocomplete"
      onChange={(val) => {
        fillProgrammeFields(val);
      }}
      enableGetChoices={({ name_tc }) => Boolean(name_tc)}
      filterToQuery={(searchText) => {
        if (searchText) {
          return { name_tc: searchText };
        }
        return {};
      }}
    >
      <AutocompleteInput optionText="name_tc" optionValue="programme_id" fullWidth />
    </ReferenceInput>
  );
};

const AutoCompleteEpisodeProgramme = () => {
  const form = useForm();

  return (
    <ReferenceInput
      label="Search Programme Name"
      source="episode_programme_autocomplete"
      reference="programmes-autocomplete"
      onChange={(programmeId) => {
        form.change("programme_id", programmeId);
        form.change("episode_id", undefined);
        form.change("destination_id", undefined);
        form.change("name_tc", undefined);
        form.change("name_en", undefined);
        form.change("landscape_poster", undefined);
        form.change("portrait_poster", undefined);
      }}
      filterToQuery={(searchText) => (searchText ? { name_tc: searchText } : {})}
    >
      <AutocompleteInput optionText="name_tc" optionValue="programme_id" fullWidth />
    </ReferenceInput>
  );
};

const EpisodeSelect = ({ programmeId }) => {
  const dataProvider = useDataProvider();
  const form = useForm();
  const [episodes, setEpisodes] = useState([]);

  useEffect(() => {
    if (!programmeId) {
      setEpisodes([]);
      return;
    }

    dataProvider
      .getList("episodes", {
        pagination: { page: 1, perPage: 1000000 },
        sort: { field: "episode_no", order: "ASC" },
        filter: { programme_id: programmeId },
      })
      .then(({ data }) => {
        setEpisodes(
          data.map((episode) => ({
            id: episode.episode_id,
            name: episode.nameWithEpisodeNo,
          })),
        );
      });
  }, [dataProvider, programmeId]);

  return (
    <AutocompleteInput
      label="Search Episode"
      source="episode_id"
      choices={episodes}
      optionText="name"
      optionValue="id"
      onChange={(episodeId) => {
        form.change("episode_id", episodeId);
        form.change("destination_id", undefined);
        form.change("name_tc", undefined);
        form.change("name_en", undefined);
        form.change("landscape_poster", undefined);
        form.change("portrait_poster", undefined);
      }}
      fullWidth
      allowEmpty
    />
  );
};

const AutoCompleteChannelName = () => {
  const form = useForm();
  const dataProvider = useDataProvider();

  const fillChannelFields = async (channelNo) => {
    if (!channelNo) {
      return;
    }

    const result = await dataProvider.getManyChannelsAutocomplete2({
      ids: [channelNo],
    });

    const channel = result?.data?.[0];

    if (channel) {
      form.change("channel_no", channel.channel_no);
      form.change("destination_id", channel.network_code);
      form.change("name_tc", channel.name_tc);
      form.change("name_en", channel.name_en);
    }
  };

  return (
    <ReferenceInput
      label="Search Channel Name"
      source="channel_autocomplete"
      reference="channels-autocomplete-2"
      onChange={(val) => {
        fillChannelFields(val);
      }}
      filterToQuery={(searchText) => {
        if (searchText) {
          return { name_tc: searchText };
        }
        return {};
      }}
    >
      <AutocompleteInput optionText="channel_text" optionValue="channel_no" fullWidth />
    </ReferenceInput>
  );
};

const AutoCompleteClip = ({ clipType }) => {
  const form = useForm();

  return (
    <ReferenceInput
      label="Search Clip Name"
      source="clip_id"
      reference="clips-autocomplete"
      onChange={(val) => {
        form.change("destination_id", undefined);
        form.change("name_tc", undefined);
        form.change("name_en", undefined);
        form.change("landscape_poster", undefined);
        form.change("portrait_poster", undefined);
        form.change("clip_id", val);
        form.change("api_timestamp", moment().valueOf());
      }}
      filterToQuery={(searchText) => {
        if (searchText) {
          return { name_tc: searchText };
        }
        return {};
      }}
      filter={{ clip_type: clipType }}
    >
      <AutocompleteInput optionText="name_tc" optionValue="clip_id" fullWidth />
    </ReferenceInput>
  );
};

const AutoCompleteTCPage = () => {
  const form = useForm();

  return (
    <ReferenceInput
      label="Search MPM Page Name TC"
      source="page_id"
      reference="mpm-page-autocomplete"
      onChange={(pageId) => {
        form.change("page_id", pageId);
        form.change("destination_id", undefined);
        form.change("name_tc", undefined);
        form.change("name_en", undefined);
        form.change("landscape_poster", undefined);
        form.change("portrait_poster", undefined);
        form.change("api_timestamp", moment().valueOf());
      }}
      filterToQuery={(searchText) => (searchText ? { name_tc: searchText } : {})}
    >
      <AutocompleteInput optionText="name_tc" optionValue="id" fullWidth />
    </ReferenceInput>
  );
};

const AutoCompleteENPage = () => {
  const form = useForm();

  return (
    <ReferenceInput
      label="Search MPM Page Name EN"
      source="page_id"
      reference="mpm-page-autocomplete"
      onChange={(pageId) => {
        form.change("page_id", pageId);
        form.change("destination_id", undefined);
        form.change("name_tc", undefined);
        form.change("name_en", undefined);
        form.change("landscape_poster", undefined);
        form.change("portrait_poster", undefined);
        form.change("api_timestamp", moment().valueOf());
      }}
      filterToQuery={(searchText) => (searchText ? { name_en: searchText } : {})}
    >
      <AutocompleteInput optionText="name_en" optionValue="id" fullWidth />
    </ReferenceInput>
  );
};

const FetchClipData = ({ clip_id, api_timestamp, name_tc, name_en, prefill = false }) => {
  const dataProvider = useDataProvider();
  const form = useForm();

  useEffect(() => {
    const fetchClip = async () => {
      if (!clip_id) {
        return;
      }

      const res = await dataProvider.getList("clips", {
        pagination: { page: 1, perPage: 100 },
        sort: { field: "id", order: "ASC" },
        filter: { clip_id: clip_id.toString() },
      });

      const clip = res.data?.[0];

      if (!clip) {
        return;
      }

      form.change("clip_type", clip.type);

      if (prefill) {
        form.change("destination_id", clip.clip_id);

        if (!name_tc) {
          form.change("name_tc", clip.name_tc);
        }

        if (!name_en) {
          form.change("name_en", clip.name_en);
        }

        form.change("landscape_poster", clip.image?.landscape_large);
        form.change("portrait_poster", clip.image?.portrait_large);
      }
    };

    fetchClip();
  }, [api_timestamp, clip_id]);

  return null;
};

const FetchPageData = ({ pageId, apiTimestamp, nameTc, nameEn }) => {
  const dataProvider = useDataProvider();
  const form = useForm();

  useEffect(() => {
    const fetchPage = async () => {
      if (!pageId) {
        return;
      }

      const { data } = await dataProvider.getList("mpm-pages", {
        pagination: { page: 1, perPage: 100 },
        sort: { field: "id", order: "ASC" },
        filter: { _id: pageId.toString() },
      });
      const page = data?.[0];

      if (!page) {
        return;
      }

      form.change("page_id", page.id);
      form.change("destination_id", page.id);

      if (!nameTc) {
        form.change("name_tc", page.name_tc);
      }

      if (!nameEn) {
        form.change("name_en", page.name_en);
      }
    };

    fetchPage();
  }, [apiTimestamp, dataProvider, form, nameEn, nameTc, pageId]);

  return null;
};

const FetchEpisodeData = ({ episodeId, nameTc, nameEn }) => {
  const dataProvider = useDataProvider();
  const form = useForm();

  useEffect(() => {
    const fetchEpisode = async () => {
      if (!episodeId) {
        return;
      }

      const { data } = await dataProvider.getList("episodes", {
        pagination: { page: 1, perPage: 100 },
        sort: { field: "episode_no", order: "ASC" },
        filter: { episode_id: Number(episodeId) },
      });
      const episode = data?.[0];

      if (!episode) {
        return;
      }

      form.change("destination_id", episode.episode_id);

      const programmeResult = await dataProvider.getProgrammeByProgrammeId(episode.programme_id);
      const programme = programmeResult?.data;
      const episodeNumberIsDate = moment(episode.episode_no, "YYYYMMDD", true).isValid();
      const formattedEpisodeDate = episodeNumberIsDate ? moment(episode.episode_no, "YYYYMMDD").format("YYYY/MM/DD") : null;

      if (!nameTc) {
        if (episode.name_tc) {
          form.change("name_tc", episode.name_tc);
        } else if (formattedEpisodeDate) {
          form.change("name_tc", `${programme?.name_tc || ""} ${formattedEpisodeDate}`.trim());
        } else if (isNaN(episode.episode_no)) {
          form.change("name_tc", `${programme?.name_tc || ""} ${episode.episode_no || ""}`.trim());
        } else {
          form.change("name_tc", `${programme?.name_tc || ""} 第${episode.episode_no}集`.trim());
        }
      }

      if (!nameEn) {
        if (episode.name_en) {
          form.change("name_en", episode.name_en);
        } else if (formattedEpisodeDate) {
          form.change("name_en", `${programme?.name_en || ""} ${formattedEpisodeDate}`.trim());
        } else if (isNaN(episode.episode_no)) {
          form.change("name_en", `${programme?.name_en || ""} ${episode.episode_no || ""}`.trim());
        } else {
          form.change("name_en", `${programme?.name_en || ""} Episode ${episode.episode_no}`.trim());
        }
      }

      form.change("landscape_poster", episode.image?.large || "");
      form.change("portrait_poster", episode.image?.large || "");
    };

    fetchEpisode();
  }, [dataProvider, episodeId, form, nameEn, nameTc]);

  return null;
};

const FormImageField = ({ id, source }) => (
  <FormDataConsumer>
    {({ formData }) => {
      if (!formData[source]) {
        return null;
      }

      return <ImageField id={id} source={source} record={formData} fullWidth />;
    }}
  </FormDataConsumer>
);

const DisplayPeriodField = ({ defaultValue = "end" }) => (
  <Grid item sm={12} md={12}>
    <RadioButtonGroupInput label="Display period" source="position" choices={displayPeriodChoices} defaultValue={defaultValue} validate={required()} />
  </Grid>
);

const ClearableTimeInput = ({ label, source }) => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });

  return (
    <TextInput
      label={label}
      source={source}
      type="time"
      InputLabelProps={{ shrink: true }}
      InputProps={{
        endAdornment: values[source] ? (
          <InputAdornment position="end" style={{ transform: "translateY(8px)" }}>
            <IconButton aria-label={`Clear ${label.toLowerCase()}`} onClick={() => form.change(source, "")} edge="end">
              <ClearIcon />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      fullWidth
    />
  );
};

const ScheduleDateTimeFields = () => (
  <>
    <Grid item sm={12} md={6}>
      <MyDateTimeInput label="Scheduled Start Date" source="start_date" showTime={false} validate={required()} fullWidth />
    </Grid>
    <Grid item sm={12} md={6}>
      <MyDateTimeInput label="Scheduled End Date" source="end_date" showTime={false} validate={required()} fullWidth />
    </Grid>
    <Grid item sm={12} md={6}>
      <ClearableTimeInput label="Programme Period Start Time" source="start_time" />
    </Grid>
    <Grid item sm={12} md={6}>
      <ClearableTimeInput label="Programme Period End Time" source="end_time" />
    </Grid>
  </>
);

const ContentTypeForm = ({ type, formData, prefillClipData = false }) => {
  if (type === "programme") return <ProgrammeForm formData={formData} />;
  if (type === "episode") return <EpisodeForm formData={formData} />;
  if (type === "page") return <PageForm formData={formData} />;
  if (type === "live") return <LiveForm />;
  if (type === "clip") return <ClipForm formData={formData} prefillClipData={prefillClipData} />;

  return null;
};

const ContentTypeSelect = ({ defaultValue }) => {
  const form = useForm();

  return (
    <SelectInput
      label="Link Content Type"
      source="type"
      choices={contentTypeChoices}
      defaultValue={defaultValue}
      fullWidth
      onChange={(event) => {
        const type = event?.target?.value ?? event;

        form.batch(() => {
          form.change("destination_id", null);
          form.change("name_en", null);
          form.change("name_tc", null);
          form.change("landscape_poster", null);
          form.change("portrait_poster", null);
          form.change("landscape_poster_base64", null);
          form.change("portrait_poster_base64", null);
          form.change("description_tc", null);
          form.change("description_en", null);
          form.change("position", type === "live" ? "start" : "end");
          form.change("version_type", type === "programme" ? "rerun" : null);
        });
      }}
    />
  );
};

export const LiveToContentCreate = (props) => {
  const notify = useNotify();
  const isClone = new URLSearchParams(props.location?.search).has("source");

  const onFailure = (error) => {
    notify(error.message.toString(), "warning");
  };

  return (
    <Create {...props} onFailure={onFailure} mutationMode="pessimistic">
      <SimpleForm toolbar={<LiveToContentCreateToolbar />} redirect={() => "/live-to-content-schedules"}>
        <Grid container spacing={1} fullWidth>
          <Grid item xs={12} md={2}>
            <TextInput label="Ch Content ID" source="network_code" fullWidth validate={required()} />
          </Grid>
          <Grid item sm={12} md={4}>
            <AutoCompleteProgramme programmeIdSource="target_epg_programme_id" />
          </Grid>
          <Grid item sm={12} md={6}>
            <TextInput label="Content ID" source="target_epg_programme_id" disabled fullWidth validate={required()} />
          </Grid>
          <Grid item sm={12} md={12}>
            <ContentTypeSelect defaultValue="programme" />
          </Grid>
          <FormDataConsumer>{({ formData }) => <ContentTypeForm type={formData.type || "programme"} formData={formData} prefillClipData={!isClone} />}</FormDataConsumer>
        </Grid>
      </SimpleForm>
    </Create>
  );
};

export const LiveToContentEdit = (props) => {
  const notify = useNotify();

  const onFailure = (error) => {
    notify(error.message.toString(), "warning");
  };

  return (
    <Edit {...props} onFailure={onFailure} mutationMode="pessimistic">
      <SimpleForm toolbar={<LiveToContentEditToolbar />} redirect={() => "/live-to-content-schedules"}>
        <FormDataConsumer>
          {({ formData }) => (
            <fieldset disabled={getRecordStatus(formData) === "end"} style={{ border: 0, margin: 0, minWidth: 0, padding: 0, pointerEvents: getRecordStatus(formData) === "end" ? "none" : "auto" }}>
              <Grid container spacing={1} fullWidth>
                <Grid item xs={12} md={2}>
                  <TextInput label="Ch Content ID" source="network_code" fullWidth validate={required()} />
                </Grid>
                <Grid item sm={12} md={4}>
                  <AutoCompleteProgramme programmeIdSource="target_epg_programme_id" />
                </Grid>
                <Grid item sm={12} md={6}>
                  <TextInput label="Content ID" source="target_epg_programme_id" disabled fullWidth validate={required()} />
                </Grid>
                <Grid item sm={12} md={12}>
                  <ContentTypeSelect />
                </Grid>
                <ContentTypeForm type={formData.type} formData={formData} />
              </Grid>
            </fieldset>
          )}
        </FormDataConsumer>
      </SimpleForm>
    </Edit>
  );
};

const LiveForm = () => {
  return (
    <Grid container spacing={1} fullWidth>
      <Grid item sm={12} md={6}>
        <AutoCompleteChannelName />
      </Grid>
      <Grid item sm={12} md={6}>
        <TextInput label="Content ID" source="destination_id" disabled fullWidth />
      </Grid>

      <Grid container spacing={1} fullWidth>
        <Grid item sm={12} md={6}>
          <TextInput label="Caption in TC" source="name_tc" fullWidth validate={required()} />
        </Grid>
        <Grid item sm={12} md={6}>
          <TextInput label="Caption in EN" source="name_en" fullWidth validate={required()} />
        </Grid>
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item sm={12} md={6}>
          <TextInput label="Link Ch Programme Name in TC" source="description_tc" validate={required()} fullWidth />
        </Grid>
        <Grid item sm={12} md={6}>
          <TextInput label="Link Ch Programme Name in EN" source="description_en" validate={required()} fullWidth />
        </Grid>
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={6}>
          <FormImageField id="portrait_poster" source="portrait_poster" />
          <ImageInput source="portrait_poster_base64" label="Portrait - 200 x 290" accept="image/*">
            <ImageField source="src" title="title" />
          </ImageInput>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <FormImageField id="landscape_poster" source="landscape_poster" />
          <ImageInput source="landscape_poster_base64" label="Landscape - 615 x 346" accept="image/*">
            <ImageField source="src" title="title" />
          </ImageInput>
        </Grid>
      </Grid>
      <DisplayPeriodField defaultValue="start" />
      <ScheduleDateTimeFields />
    </Grid>
  );
};

const ClipForm = ({ formData, prefillClipData }) => {
  return (
    <Grid container spacing={1} fullWidth>
      <FetchClipData {...formData} clip_id={formData.clip_id ?? formData.destination_id} prefill={prefillClipData} />
      <Grid item sm={12} md={12}>
        <ReferenceInput label="Clip Type" source="clip_type" reference="clips-type" fullWidth>
          <SelectInput optionText="name" optionValue="id" />
        </ReferenceInput>
      </Grid>
      <Grid item sm={12} md={6}>
        <AutoCompleteClip clipType={formData.clip_type} />
      </Grid>
      <Grid item sm={12} md={6}>
        <TextInput label="Content ID" source="destination_id" disabled fullWidth />
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item sm={12} md={6}>
          <TextInput label="Caption in TC" source="name_tc" fullWidth validate={required()} />
        </Grid>
        <Grid item sm={12} md={6}>
          <TextInput label="Caption in EN" source="name_en" fullWidth validate={required()} />
        </Grid>
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={6}>
          <FormImageField id="portrait_poster" source="portrait_poster" />
          <ImageInput source="portrait_poster_base64" label="Portrait - 200 x 290" accept="image/*">
            <ImageField source="src" title="title" />
          </ImageInput>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <FormImageField id="landscape_poster" source="landscape_poster" />
          <ImageInput source="landscape_poster_base64" label="Landscape - 615 x 346" accept="image/*">
            <ImageField source="src" title="title" />
          </ImageInput>
        </Grid>
      </Grid>
      <DisplayPeriodField />
      <ScheduleDateTimeFields />
    </Grid>
  );
};

const ProgrammeForm = () => {
  return (
    <Grid container spacing={1} fullWidth>
      <Grid item sm={12} md={6}>
        <AutoCompleteProgramme autocompleteSource="destination_programme_autocomplete" captionTcSource="name_tc" captionEnSource="name_en" landscapePosterSource="landscape_poster" portraitPosterSource="portrait_poster" />
      </Grid>
      <Grid item sm={12} md={6}>
        <TextInput label="Content ID" source="destination_id" disabled fullWidth />
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item sm={12} md={6}>
          <TextInput label="Caption in TC" source="name_tc" fullWidth validate={required()} />
        </Grid>
        <Grid item sm={12} md={6}>
          <TextInput label="Caption in EN" source="name_en" fullWidth validate={required()} />
        </Grid>
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={6}>
          <FormImageField id="portrait_poster" source="portrait_poster" />
          <ImageInput source="portrait_poster_base64" label="Portrait - 200 x 290" accept="image/*">
            <ImageField source="src" title="title" />
          </ImageInput>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <FormImageField id="landscape_poster" source="landscape_poster" />
          <ImageInput source="landscape_poster_base64" label="Landscape - 615 x 346" accept="image/*">
            <ImageField source="src" title="title" />
          </ImageInput>
        </Grid>
      </Grid>
      <Grid item sm={12} md={12}>
        <RadioButtonGroupInput label="Version Type" source="version_type" choices={versionTypeChoices} defaultValue="rerun" validate={required()} />
      </Grid>
      <DisplayPeriodField />
      <ScheduleDateTimeFields />
    </Grid>
  );
};

const EpisodeForm = ({ formData }) => {
  return (
    <Grid container spacing={1} fullWidth>
      <FetchEpisodeData episodeId={formData.episode_id} nameTc={formData.name_tc} nameEn={formData.name_en} />
      <Grid item sm={12} md={4}>
        <AutoCompleteEpisodeProgramme />
      </Grid>
      <Grid item sm={12} md={4}>
        <EpisodeSelect programmeId={formData.programme_id} />
      </Grid>
      <Grid item sm={12} md={4}>
        <TextInput label="Content ID" source="destination_id" disabled fullWidth />
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item sm={12} md={6}>
          <TextInput label="Caption in TC" source="name_tc" validate={required()} fullWidth />
        </Grid>
        <Grid item sm={12} md={6}>
          <TextInput label="Caption in EN" source="name_en" validate={required()} fullWidth />
        </Grid>
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={6}>
          <FormImageField id="portrait_poster" source="portrait_poster" />
          <ImageInput source="portrait_poster_base64" label="Portrait - 200 x 290" accept="image/*">
            <ImageField source="src" title="title" />
          </ImageInput>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <FormImageField id="landscape_poster" source="landscape_poster" />
          <ImageInput source="landscape_poster_base64" label="Landscape - 615 x 346" accept="image/*">
            <ImageField source="src" title="title" />
          </ImageInput>
        </Grid>
      </Grid>
      <DisplayPeriodField />
      <ScheduleDateTimeFields />
    </Grid>
  );
};

const PageForm = ({ formData }) => {
  return (
    <Grid container spacing={1} fullWidth>
      <FetchPageData pageId={formData.page_id ?? formData.destination_id} apiTimestamp={formData.api_timestamp} nameTc={formData.name_tc} nameEn={formData.name_en} />
      <Grid item sm={12} md={6}>
        <AutoCompleteTCPage />
      </Grid>
      <Grid item sm={12} md={6}>
        <AutoCompleteENPage />
      </Grid>
      <Grid item sm={12} md={12}>
        <TextInput label="Content ID" source="destination_id" disabled fullWidth />
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item sm={12} md={6}>
          <TextInput label="Caption in TC" source="name_tc" validate={required()} fullWidth />
        </Grid>
        <Grid item sm={12} md={6}>
          <TextInput label="Caption in EN" source="name_en" validate={required()} fullWidth />
        </Grid>
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={6}>
          <FormImageField id="portrait_poster" source="portrait_poster" />
          <ImageInput source="portrait_poster_base64" label="Portrait - 200 x 290" accept="image/*">
            <ImageField source="src" title="title" />
          </ImageInput>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <FormImageField id="landscape_poster" source="landscape_poster" />
          <ImageInput source="landscape_poster_base64" label="Landscape - 615 x 346" accept="image/*">
            <ImageField source="src" title="title" />
          </ImageInput>
        </Grid>
      </Grid>
      <DisplayPeriodField />
      <ScheduleDateTimeFields />
    </Grid>
  );
};
